// src/components/FormSettings/FormBuilder.jsx
import React from 'react';
import { Outlet, useLocation } from 'react-router-dom'; // Import Outlet
import FormBuilderToolbar from '../Navbar/FormBuilderToolbar';

/**
 * FormBuilder
 * -----------------------------------------------------------------------------
 * Shared shell for nested form-builder routes.
 *
 * IMPORTANT ENTERPRISE LAYOUT NOTE
 *  • Most existing form-builder pages currently manage their own page height
 *    and/or document scrolling behavior.
 *  • Lists V2 is different because it contains a spreadsheet-like surface
 *    that must own its own vertical and horizontal scrolling.
 *
 * WHY THIS ROUTE-SPECIFIC SHELL EXISTS
 *  • We do not want to change the scroll contract for every existing builder
 *    route while Lists V2 is being rolled out.
 *  • For /lists and the temporary /lists-lab alias, we provide a bounded flex
 *    viewport:
 *      Navbar outside this component: 48px
 *      Toolbar inside this component: 48px
 *      Outlet area: flex: 1; minHeight: 0; overflow: hidden
 *  • The spreadsheet canvas then owns the internal scroll.
 *
 * FUTURE CLEANUP
 *  • Once all builder pages are aligned to a common app-shell layout, this can
 *    become the default route layout instead of a Lists-specific branch.
 */
function FormBuilder({ isPreview }) {
  const location = useLocation();

  /**
   * Lists V2 bounded viewport routes
   * ---------------------------------------------------------------------------
   * /lists is the production route after replacing the old Lists page.
   * /lists-lab remains a temporary rollout/testing alias.
   *
   * IMPORTANT
   *  • Both routes must use the same shell layout.
   *  • If only /lists-lab uses this layout, /lists will render the same
   *    FormListsLabPage inside the legacy unbounded builder shell, causing:
   *      - sidepanel background not stretching to full height
   *      - collapsed rail background stopping early
   *      - spreadsheet scroll/viewport behavior differences
   */
  const isListsSpreadsheetRoute =
    /\/form-builder\/[^/]+\/lists(?:-lab)?\/?$/.test(
      location.pathname,
    );

  if (isListsSpreadsheetRoute && !isPreview) {
    return (
      <div
        style={{
          '--form-builder-navbar-height': '48px',
          '--form-builder-toolbar-height': '48px',
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - var(--form-builder-navbar-height))',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            flex: '0 0 var(--form-builder-toolbar-height)',
            minHeight: 'var(--form-builder-toolbar-height)',
          }}
        >
          <FormBuilderToolbar />
        </div>

        <div
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            minWidth: 0,
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          <Outlet />
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Only show the toolbar if NOT in preview mode */}
      {!isPreview && <FormBuilderToolbar />}
      <Outlet /> {/* Nested routes will render here */}
    </div>
  );
}

export default FormBuilder;